import type { SupabaseClient } from '@supabase/supabase-js';
import { getMeta, setMeta } from '../data/appMeta';
import type { SqlDriver } from '../data/db/driver';
import { rpcTransport } from './rpc';
import type { SyncTransport } from './types';

/**
 * The account and the network, behind one small interface. The app never
 * needs it to work: without a configured cloud (or signed out) everything
 * stays on the device. Sign-in is a 6-digit code sent by e-mail: one method
 * that is the same account on iPhone, Android and the web.
 */
export interface Account {
  email: string;
}

export interface Cloud {
  transport: SyncTransport;
  account(): Promise<Account | null>;
  sendCode(email: string): Promise<void>;
  /** Signs in; throws when the code is wrong or expired. */
  verifyCode(email: string, code: string): Promise<Account>;
  signOut(): Promise<void>;
}

export interface CloudConfig {
  url: string;
  anonKey: string;
}

/** Build-time settings (VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY); absent = sync is not part of this build. */
export function cloudConfig(env: Record<string, string | undefined> = import.meta.env): CloudConfig | null {
  const url = env.VITE_SUPABASE_URL?.trim();
  const anonKey = env.VITE_SUPABASE_ANON_KEY?.trim();
  return url && anonKey ? { url, anonKey } : null;
}

/** The key supabase-js stores its session under: sb-<project ref>-auth-token. */
export function storageKey(config: CloudConfig): string {
  const ref = new URL(config.url).hostname.split('.')[0];
  return `sb-${ref}-auth-token`;
}

/** Supabase keeps its session in the app's own SQLite (survives WebView storage clean-ups). */
function sessionStorageIn(db: SqlDriver) {
  const key = (k: string) => `cloud.${k}`;
  return {
    getItem: async (k: string) => (await getMeta(db, key(k))) ?? null,
    setItem: async (k: string, v: string) => setMeta(db, key(k), v),
    removeItem: async (k: string) => {
      await db.run('DELETE FROM app_meta WHERE key = ?', [key(k)]);
    },
  };
}

export function supabaseTransport(getClient: () => Promise<SupabaseClient>): SyncTransport {
  return rpcTransport(async (fn, args) => {
    const { data, error } = await (await getClient()).rpc(fn, args);
    if (error) throw new Error(`${fn}: ${error.message}`);
    return data;
  });
}

/**
 * The Supabase library (≈ 230 kB) is loaded on first use, not at startup:
 * someone who never signs in never downloads it, and the app opens as fast
 * as it did before sync existed.
 */
export function createSupabaseCloud(db: SqlDriver, config: CloudConfig): Cloud {
  let clientPromise: Promise<SupabaseClient> | null = null;
  const getClient = () =>
    (clientPromise ??= import('@supabase/supabase-js').then(({ createClient }) =>
      createClient(config.url, config.anonKey, {
        auth: { storage: sessionStorageIn(db), persistSession: true, autoRefreshToken: true, detectSessionInUrl: false },
      }),
    ));
  const accountOf = (email: string | undefined | null): Account | null => (email ? { email } : null);
  return {
    transport: supabaseTransport(getClient),
    async account() {
      // No stored session: no need to load the library at all.
      if (!(await getMeta(db, 'cloud.' + storageKey(config)))) return null;
      const client = await getClient();
      const { data } = await client.auth.getSession();
      return accountOf(data.session?.user.email);
    },
    async sendCode(email) {
      const client = await getClient();
      const { error } = await client.auth.signInWithOtp({ email, options: { shouldCreateUser: true } });
      if (error) throw error;
    },
    async verifyCode(email, code) {
      const client = await getClient();
      const { data, error } = await client.auth.verifyOtp({ email, token: code, type: 'email' });
      if (error) throw error;
      return accountOf(data.user?.email ?? email)!;
    },
    async signOut() {
      await (await getClient()).auth.signOut({ scope: 'local' });
    },
  };
}
